import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Button, Card, CardContent, FormControl, Grid,
  TextField, Typography, Alert, Snackbar, Autocomplete,
} from "@mui/material";
import { DateTimePicker } from "@mui/lab";
import { Formik } from "formik";
import * as Yup from "yup";
import axios from "../../CustomAxios";
import Loading from "../components/Loading";
import { connect } from "react-redux";

const PlatePriceScheduleEdit = ({ auths }) => {
  const authData = JSON.parse(localStorage.getItem("AuthData"));
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState(null);
  const [loader, setLoader] = useState(true);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
        const { data } = await axios.get(`/api/v1/${authData.type}/plate_price_schedules/${id}`, config);
        if (data.success) {
          const schedule = data.data;
          setInitialValues({
            baking_cost: schedule.baking_cost,
            print_cost: schedule.print_cost,
            selling_cost: schedule.selling_cost,
            gripper: schedule.gripper,
            effective_date: new Date(schedule.effective_date),
            is_active: schedule.is_active,
          });
        }
      } catch (e) { console.error(e); }
      setLoader(false);
    };
    fetchSchedule();
  }, [id, authData]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoader(true);
      const config = { headers: { Authorization: `Bearer ${authData.access_token}` } };
      await axios.put(`/api/v1/${authData.type}/plate_price_schedules/${id}`, values, config);
      setSnack({ open: true, message: "Schedule updated", severity: "success" });
      navigate("/dashboard/plate-price-schedules");
    } catch (error) {
      setSnack({ open: true, message: error.response?.data?.error || "Error", severity: "error" });
    }
    setLoader(false);
    setSubmitting(false);
  };

  if (loader) return <Loading />;

  return (
    <>
      <Helmet title="Edit Effective Cost" />
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }} color="primary">
            Edit Effective Cost
          </Typography>
        </Grid>
      </Grid>

      {initialValues && (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={Yup.object({
            baking_cost: Yup.number().required("Required"),
            print_cost: Yup.number().required("Required"),
            selling_cost: Yup.number().required("Required"),
            gripper: Yup.number().min(0).max(99.99).required("Required"),
            effective_date: Yup.date().required("Required"),
          })}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Card>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        name="baking_cost"
                        label="Baking Cost"
                        type="number"
                        value={values.baking_cost}
                        onChange={handleChange}
                        error={touched.baking_cost && !!errors.baking_cost}
                        helperText={touched.baking_cost && errors.baking_cost}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        name="print_cost"
                        label="Print Cost"
                        type="number"
                        value={values.print_cost}
                        onChange={handleChange}
                        error={touched.print_cost && !!errors.print_cost}
                        helperText={touched.print_cost && errors.print_cost}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        name="selling_cost"
                        label="Selling Cost"
                        type="number"
                        value={values.selling_cost}
                        onChange={handleChange}
                        error={touched.selling_cost && !!errors.selling_cost}
                        helperText={touched.selling_cost && errors.selling_cost}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        name="gripper"
                        label="Gripper"
                        type="number"
                        inputProps={{ step: "0.01", min: 0, max: 99.99 }}
                        value={values.gripper}
                        onChange={handleChange}
                        error={touched.gripper && !!errors.gripper}
                        helperText={touched.gripper && errors.gripper}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <DateTimePicker
                          label="Effective Date"
                          value={values.effective_date}
                          onChange={(newValue) => setFieldValue("effective_date", newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={touched.effective_date && !!errors.effective_date}
                              helperText={touched.effective_date && errors.effective_date}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                        Update Schedule
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </form>
          )}
        </Formik>
      )}

      <Snackbar open={snack.open} autoHideDuration={6000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </>
  );
};

const mapStateToProps = (state) => ({ auths: state.auths });
export default connect(mapStateToProps)(PlatePriceScheduleEdit);